(() => {
  'use strict';

  if (window.__ilbShotBallRegistered) return;
  window.__ilbShotBallRegistered = true;

  const scrollRecords = new WeakMap();
  const heroScrubRecords = new WeakMap();
  const anatomyScrubRecords = new WeakMap();
  const readerRecords = new WeakMap();
  const youtubeRecords = new WeakMap();
  const actionRecords = new WeakMap();

  const findAll = (root, selector) => {
    const matches = [];
    if (root instanceof Element && root.matches(selector)) matches.push(root);
    if (root.querySelectorAll) matches.push(...root.querySelectorAll(selector));
    return matches;
  };

  const initScrollSpin = (root) => {
    if (scrollRecords.has(root)) return;

    const video = root.querySelector('[data-ilb-scroll-video]');
    const range = root.querySelector('[data-ilb-scroll-range]');
    const enable = root.querySelector('[data-ilb-scroll-enable]');
    const hint = root.querySelector('[data-ilb-scroll-hint]');
    const status = root.querySelector('[data-ilb-scroll-status]');
    const stageCopies = [...root.querySelectorAll('[data-ilb-scroll-stage-copy]')];
    if (!video || !range || !enable || !hint || !status || stageCopies.length !== 3) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMedia = window.matchMedia('(max-width: 680px)');
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const fps = 24;
    const frameDuration = 1 / fps;
    let duration = Number(root.dataset.duration) || 0;
    let attached = false;
    let frameRequest = 0;
    let observer = null;
    let rangeActive = false;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const getScrollProgress = () => {
      const rect = root.getBoundingClientRect();
      const travel = Math.max(1, root.offsetHeight - window.innerHeight);
      return clamp(-rect.top / travel, 0, 1);
    };

    const setStage = (progress) => {
      const stage = progress < .34 ? 0 : progress < .72 ? 1 : 2;
      const valueText = stage === 0 ? 'Individual markers' : stage === 1 ? 'Building the visible line' : 'Visible line';
      stageCopies.forEach((item, index) => item.classList.toggle('is-active', index === stage));
      range.setAttribute('aria-valuetext', valueText);
    };

    const applyProgress = (progress) => {
      const safeProgress = clamp(progress, 0, 1);
      root.style.setProperty('--ilb-scroll-progress', safeProgress.toFixed(4));
      range.value = String(Math.round(safeProgress * 100));
      setStage(safeProgress);

      if (!attached || !duration || video.readyState < 1) return;
      const usableDuration = Math.max(0, duration - frameDuration);
      const target = Math.round(safeProgress * usableDuration * fps) / fps;
      if (Math.abs(video.currentTime - target) >= frameDuration * .5) video.currentTime = target;
    };

    const render = () => {
      frameRequest = 0;
      if (!rangeActive && root.classList.contains('is-enhanced')) applyProgress(getScrollProgress());
    };

    const schedule = () => {
      if (!frameRequest) frameRequest = window.requestAnimationFrame(render);
    };

    const attachSource = () => {
      if (attached) return;
      attached = true;
      video.src = mobileMedia.matches ? root.dataset.mobileSrc : root.dataset.desktopSrc;
      video.preload = 'auto';
      video.load();
      status.textContent = 'Loading the interactive rotation…';
    };

    video.addEventListener('loadedmetadata', () => {
      duration = video.duration;
      applyProgress(root.classList.contains('is-enhanced') ? getScrollProgress() : Number(range.value) / 100);
    }, { signal });

    video.addEventListener('loadeddata', () => {
      root.classList.add('is-media-ready');
      status.textContent = '';
      schedule();
    }, { signal });

    video.addEventListener('error', () => {
      root.classList.add('is-media-error');
      root.classList.remove('is-enhanced', 'is-manual');
      range.disabled = true;
      hint.textContent = 'Interactive rotation is unavailable. The product view remains visible.';
      status.textContent = 'Interactive rotation is unavailable.';
    }, { signal });

    range.addEventListener('pointerdown', () => { rangeActive = true; }, { signal });
    range.addEventListener('input', () => {
      rangeActive = true;
      if (!attached) attachSource();
      if (!root.classList.contains('is-enhanced')) root.classList.add('is-manual');
      applyProgress(Number(range.value) / 100);
    }, { signal });
    range.addEventListener('change', () => {
      rangeActive = false;
      schedule();
    }, { signal });
    range.addEventListener('blur', () => {
      rangeActive = false;
      schedule();
    }, { signal });

    enable.addEventListener('click', () => {
      root.classList.add('is-manual');
      enable.hidden = true;
      hint.textContent = 'Use the slider to control the rotation.';
      attachSource();
    }, { signal });

    if (reduceMotion.matches || saveData || designMode) {
      root.classList.add('is-static');
      enable.hidden = false;
      hint.textContent = reduceMotion.matches
        ? 'Motion is paused to respect your settings.'
        : 'Interactive rotation is paused until you choose to load it.';
    } else {
      root.classList.add('is-enhanced');
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          attachSource();
          observer.disconnect();
          observer = null;
        }, { rootMargin: '150% 0px' });
        observer.observe(root);
      } else {
        attachSource();
      }
      schedule();
    }

    scrollRecords.set(root, {
      destroy() {
        controller.abort();
        if (observer) observer.disconnect();
        if (frameRequest) window.cancelAnimationFrame(frameRequest);
        video.pause();
        video.removeAttribute('src');
        video.load();
        root.classList.remove('is-enhanced', 'is-manual', 'is-media-ready', 'is-media-error', 'is-static');
        scrollRecords.delete(root);
      }
    });
  };

  const initHeroScrub = (root) => {
    if (heroScrubRecords.has(root)) return;

    const video = root.querySelector('[data-ilb-hero-scrub-video]');
    const status = root.querySelector('[data-ilb-hero-scrub-status]');
    const stage = root.querySelector('[data-ilb-hero-scrub-stage]');
    const jump = root.querySelector('.ilb-shot-ball__hero-product-jump');
    if (!video) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMedia = window.matchMedia('(max-width: 680px)');
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const fps = 24;
    const frameDuration = 1 / fps;
    const titleSequenceStartTime = 4.9;
    const titleSequenceEndTime = 8.35;
    const titleSequenceScrollStart = .715;
    const titleSequenceScrollEnd = .8;
    let duration = Number(root.dataset.duration) || 0;
    let attached = false;
    let frameRequest = 0;
    let observer = null;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const positionJump = () => {
      if (!jump || !stage) return;

      const stageWidth = stage.clientWidth;
      const stageHeight = stage.clientHeight;
      const sourceWidth = video.videoWidth || 1920;
      const sourceHeight = video.videoHeight || 1080;
      if (!stageWidth || !stageHeight || !sourceWidth || !sourceHeight) return;

      const scale = Math.max(stageWidth / sourceWidth, stageHeight / sourceHeight);
      const renderedWidth = sourceWidth * scale;
      const renderedHeight = sourceHeight * scale;
      const offsetX = (stageWidth - renderedWidth) / 2;
      const offsetY = (stageHeight - renderedHeight) / 2;

      // The CTA is part of the video artwork. Project its source-frame box into
      // the current object-fit: cover crop so the invisible link stays aligned.
      stage.style.setProperty('--ilb-hero-cta-left', `${offsetX + renderedWidth * .891}px`);
      stage.style.setProperty('--ilb-hero-cta-top', `${offsetY + renderedHeight * .758}px`);
      stage.style.setProperty('--ilb-hero-cta-width', `${renderedWidth * .109}px`);
      stage.style.setProperty('--ilb-hero-cta-height', `${renderedHeight * .069}px`);
    };

    const getScrollProgress = () => {
      const rect = root.getBoundingClientRect();
      const travel = Math.max(1, root.offsetHeight - window.innerHeight);
      const stickyOffset = stage ? Number.parseFloat(window.getComputedStyle(stage).top) || 0 : 0;
      return clamp((stickyOffset - rect.top) / travel, 0, 1);
    };

    const applyProgress = (progress) => {
      const safeProgress = clamp(progress, 0, 1);
      root.style.setProperty('--ilb-hero-progress', safeProgress.toFixed(4));
      if (!attached || !duration || video.readyState < 1) return;

      const usableDuration = Math.max(0, duration - frameDuration);
      // The source begins with a restrained rotation. Give the opening portion
      // more scroll sensitivity so the first wheel/touch movement is visible,
      // then ease smoothly through the remainder of the full video.
      const responsiveProgress = Math.pow(safeProgress, .78);
      let sourceTime = responsiveProgress * usableDuration;

      // Preserve the complete title sequence, including the ball leaving and
      // returning, while assigning it a short, readable portion of the scroll.
      if (usableDuration > titleSequenceEndTime) {
        if (responsiveProgress <= titleSequenceScrollStart) {
          sourceTime = (responsiveProgress / titleSequenceScrollStart) * titleSequenceStartTime;
        } else if (responsiveProgress <= titleSequenceScrollEnd) {
          const titleProgress = (responsiveProgress - titleSequenceScrollStart)
            / (titleSequenceScrollEnd - titleSequenceScrollStart);
          sourceTime = titleSequenceStartTime
            + titleProgress * (titleSequenceEndTime - titleSequenceStartTime);
        } else {
          const returnProgress = (responsiveProgress - titleSequenceScrollEnd)
            / (1 - titleSequenceScrollEnd);
          sourceTime = titleSequenceEndTime
            + returnProgress * (usableDuration - titleSequenceEndTime);
        }
      }

      const target = Math.round(sourceTime * fps) / fps;
      if (Math.abs(video.currentTime - target) >= frameDuration * .5) video.currentTime = target;
    };

    const render = () => {
      frameRequest = 0;
      if (root.classList.contains('is-enhanced')) applyProgress(getScrollProgress());
    };

    const schedule = () => {
      if (!frameRequest) frameRequest = window.requestAnimationFrame(render);
    };

    const attachSource = () => {
      if (attached) return;
      attached = true;
      video.src = mobileMedia.matches ? root.dataset.mobileSrc : root.dataset.desktopSrc;
      video.preload = 'auto';
      video.load();
      if (status) status.textContent = 'Loading the scroll-controlled product view.';
    };

    const observeSource = () => {
      if (attached || observer) return;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          attachSource();
          observer.disconnect();
          observer = null;
        }, { rootMargin: '100% 0px' });
        observer.observe(root);
      } else {
        attachSource();
      }
    };

    const setMotionMode = () => {
      const interactive = !mobileMedia.matches && !reduceMotion.matches && !saveData && !designMode;
      root.classList.toggle('is-enhanced', interactive);
      root.classList.toggle('is-static', !interactive);

      if (interactive) {
        if (status) status.textContent = 'Scroll to control the basketball motion.';
        observeSource();
        schedule();
      } else {
        if (frameRequest) {
          window.cancelAnimationFrame(frameRequest);
          frameRequest = 0;
        }
        applyProgress(0);
        if (status) status.textContent = reduceMotion.matches
          ? 'Motion is paused to respect your settings.'
          : 'The interactive hero is paused to reduce data use.';
      }
    };

    video.addEventListener('loadedmetadata', () => {
      duration = Math.min(video.duration, Number(root.dataset.duration) || video.duration);
      video.pause();
      positionJump();
      applyProgress(getScrollProgress());
    }, { signal });

    video.addEventListener('loadeddata', () => {
      root.classList.add('is-media-ready');
      if (status) status.textContent = 'Scroll to control the basketball motion.';
      schedule();
    }, { signal });

    video.addEventListener('error', () => {
      root.classList.add('is-media-error', 'is-static');
      root.classList.remove('is-enhanced');
      if (status) status.textContent = 'The interactive hero is unavailable. The product poster remains visible.';
    }, { signal });

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', () => {
      positionJump();
      schedule();
    }, { passive: true, signal });
    reduceMotion.addEventListener('change', setMotionMode, { signal });
    mobileMedia.addEventListener('change', setMotionMode, { signal });
    positionJump();
    setMotionMode();

    heroScrubRecords.set(root, {
      destroy() {
        controller.abort();
        if (observer) observer.disconnect();
        if (frameRequest) window.cancelAnimationFrame(frameRequest);
        video.pause();
        video.removeAttribute('src');
        video.load();
        if (stage) {
          stage.style.removeProperty('--ilb-hero-cta-left');
          stage.style.removeProperty('--ilb-hero-cta-top');
          stage.style.removeProperty('--ilb-hero-cta-width');
          stage.style.removeProperty('--ilb-hero-cta-height');
        }
        root.style.removeProperty('--ilb-hero-progress');
        root.classList.remove('is-enhanced', 'is-static', 'is-media-ready', 'is-media-error');
        heroScrubRecords.delete(root);
      }
    });
  };

  const initAnatomyScrub = (root) => {
    if (anatomyScrubRecords.has(root)) return;

    const video = root.querySelector('[data-ilb-anatomy-scrub-video]');
    const status = root.querySelector('[data-ilb-anatomy-scrub-status]');
    const track = root.closest('[data-ilb-anatomy-scrub-track]') || root;
    const copy = root.closest('.ilb-shot-ball__anatomy')?.querySelector('.ilb-shot-ball__anatomy-copy');
    if (!video || !root.dataset.mobileSrc) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMedia = window.matchMedia('(max-width: 680px)');
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const fps = 24;
    const frameDuration = 1 / fps;
    let duration = Number(root.dataset.duration) || 0;
    let attached = false;
    let frameRequest = 0;
    let observer = null;
    let snapTimer = 0;
    let hasSnapped = false;
    let lastSafeProgress = 0;
    let lastScrollY = window.scrollY;
    let scrollingDown = true;
    let targetTime = 0;
    let lastSeekAt = 0;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const getScrollProgress = () => {
      const trackRect = track.getBoundingClientRect();
      const viewportEntry = window.innerHeight * .98;
      const stickyTop = 0;
      const stickyRunway = Math.max(0, track.offsetHeight - root.offsetHeight);
      const travel = Math.max(760, (viewportEntry - stickyTop) + (stickyRunway * .96));
      return clamp((viewportEntry - trackRect.top) / travel, 0, 1);
    };

    const applyProgress = (progress) => {
      // The source video already contains its own slow-to-fast-to-slow motion.
      // Keep scroll-to-time linear so we do not double-ease the rotation.
      const rawProgress = clamp(progress, 0, 1);
      const safeProgress = rawProgress;
      lastSafeProgress = safeProgress;
      if (safeProgress < .9) hasSnapped = false;
      root.style.setProperty('--ilb-anatomy-progress', safeProgress.toFixed(4));
      if (!attached || !duration || video.readyState < 1) return;
      const usableDuration = Math.max(0, duration - frameDuration);
      targetTime = Math.round(safeProgress * usableDuration * fps) / fps;
    };

    const followTargetTime = (now) => {
      if (!attached || !duration || video.readyState < 1) return false;
      const difference = targetTime - video.currentTime;
      // Browsers can settle one decoded frame before the requested timestamp.
      // Treat that as complete so the follow loop and completion handoff do not stall.
      if (Math.abs(difference) <= frameDuration * 1.25) {
        lastSeekAt = 0;
        return false;
      }

      if (video.seeking) return true;
      const elapsed = lastSeekAt ? now - lastSeekAt : 80;
      if (lastSeekAt && elapsed < 72) return true;
      const constrainedElapsed = clamp(elapsed, 16, 120);
      const easedStep = difference * (1 - Math.exp(-constrainedElapsed / 220));
      const maxStep = Math.max(frameDuration, constrainedElapsed * .0045);
      const step = clamp(easedStep, -maxStep, maxStep);
      const nextTime = Math.round((video.currentTime + step) * fps) / fps;
      if (Math.abs(video.currentTime - nextTime) >= frameDuration * .5) {
        lastSeekAt = now;
        video.currentTime = nextTime;
      }
      return true;
    };

    const queueCompletionSnap = () => {
      const cancelSnap = () => {
        if (!snapTimer) return;
        window.clearTimeout(snapTimer);
        snapTimer = 0;
      };
      if (!copy || hasSnapped || !scrollingDown || lastSafeProgress < .999 || video.readyState < 1) {
        cancelSnap();
        return;
      }
      const usableDuration = Math.max(0, duration - frameDuration);
      if (video.currentTime < usableDuration - (frameDuration * 2)) {
        cancelSnap();
        return;
      }
      const copyRect = copy.getBoundingClientRect();
      if (copyRect.top < window.innerHeight * -.55 || copyRect.top > window.innerHeight * 1.25) {
        cancelSnap();
        return;
      }
      if (snapTimer) return;

      snapTimer = window.setTimeout(() => {
        snapTimer = 0;
        if (hasSnapped || !scrollingDown || lastSafeProgress < .999) return;
        const settledRect = copy.getBoundingClientRect();
        if (settledRect.top < window.innerHeight * -.55 || settledRect.top > window.innerHeight * 1.25) return;
        hasSnapped = true;
        window.scrollTo({ top: window.scrollY + settledRect.top, behavior: 'smooth' });
      }, 140);
    };

    const render = (now) => {
      frameRequest = 0;
      if (root.classList.contains('is-anatomy-scrub-enabled')) {
        applyProgress(getScrollProgress());
        const isFollowing = followTargetTime(now);
        queueCompletionSnap();
        if (isFollowing) frameRequest = window.requestAnimationFrame(render);
      }
    };

    const schedule = () => {
      if (!frameRequest) frameRequest = window.requestAnimationFrame(render);
    };

    const attachSource = () => {
      if (attached) return;
      attached = true;
      video.src = root.dataset.mobileSrc;
      video.preload = 'auto';
      video.load();
      if (status) status.textContent = 'Loading the scroll-controlled basketball view.';
    };

    const observeSource = () => {
      if (attached || observer) return;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          attachSource();
          observer.disconnect();
          observer = null;
        }, { rootMargin: '10% 0px' });
        observer.observe(root);
      } else {
        attachSource();
      }
    };

    const setMotionMode = () => {
      const interactive = mobileMedia.matches && !reduceMotion.matches && !saveData && !designMode;
      root.classList.toggle('is-anatomy-scrub-enabled', interactive);
      root.classList.toggle('is-anatomy-scrub-static', !interactive);
      track.classList.toggle('is-anatomy-scrub-track-enabled', interactive);

      if (interactive) {
        if (status) status.textContent = 'Scroll to control the basketball rotation.';
        observeSource();
        schedule();
      } else {
        if (frameRequest) {
          window.cancelAnimationFrame(frameRequest);
          frameRequest = 0;
        }
        if (status) status.textContent = reduceMotion.matches
          ? 'Motion is paused to respect your settings.'
          : 'A static product view is shown.';
      }
    };

    video.addEventListener('loadedmetadata', () => {
      duration = Math.min(video.duration, Number(root.dataset.duration) || video.duration);
      video.pause();
      applyProgress(getScrollProgress());
    }, { signal });

    video.addEventListener('loadeddata', () => {
      root.classList.add('is-anatomy-media-ready');
      if (status) status.textContent = 'Scroll to control the basketball rotation.';
      schedule();
    }, { signal });

    video.addEventListener('seeked', schedule, { signal });

    video.addEventListener('error', () => {
      root.classList.add('is-anatomy-media-error', 'is-anatomy-scrub-static');
      root.classList.remove('is-anatomy-scrub-enabled', 'is-anatomy-media-ready');
      track.classList.remove('is-anatomy-scrub-track-enabled');
      if (status) status.textContent = 'The rotating product view is unavailable. A static product image is shown.';
    }, { signal });

    window.addEventListener('scroll', () => {
      const nextScrollY = window.scrollY;
      scrollingDown = nextScrollY >= lastScrollY;
      lastScrollY = nextScrollY;
      schedule();
    }, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    reduceMotion.addEventListener('change', setMotionMode, { signal });
    mobileMedia.addEventListener('change', setMotionMode, { signal });
    setMotionMode();

    anatomyScrubRecords.set(root, {
      destroy() {
        controller.abort();
        if (observer) observer.disconnect();
        if (frameRequest) window.cancelAnimationFrame(frameRequest);
        if (snapTimer) window.clearTimeout(snapTimer);
        video.pause();
        video.removeAttribute('src');
        video.load();
        root.style.removeProperty('--ilb-anatomy-progress');
        root.classList.remove('is-anatomy-scrub-enabled', 'is-anatomy-scrub-static', 'is-anatomy-media-ready', 'is-anatomy-media-error');
        track.classList.remove('is-anatomy-scrub-track-enabled');
        anatomyScrubRecords.delete(root);
      }
    });
  };

  const initReader = (root) => {
    if (readerRecords.has(root)) return;
    const controls = root.querySelector('[data-ilb-signal-controls]');
    const buttons = [...root.querySelectorAll('[data-ilb-signal]')];
    const panels = [...root.querySelectorAll('[data-ilb-signal-panel]')];
    const section = root.closest('[data-ilb-training]');
    const status = section && section.querySelector('[data-ilb-signal-status]');
    const scrollTrack = root.closest('[data-ilb-signal-scroll-track]');
    if (!controls || !status || buttons.length < 2 || panels.length !== buttons.length) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMedia = window.matchMedia('(max-width: 680px)');
    const autoplayEligible = Boolean(root.hasAttribute('data-ilb-visual-example-reader')
      && buttons.length === 5);
    const autoplayDelay = 5000;
    let requestToken = 0;
    let autoplayTimer = 0;
    let observer = null;
    let isNearViewport = false;
    let isHovered = false;
    let hasFocusWithin = false;
    let manualStopped = false;
    let discoveryTimer = 0;
    let scrollFrameRequest = 0;
    let scrollDrivenIndex = -1;
    let scrollDriven = false;
    let removeReduceMotionListener = () => {};
    controls.hidden = false;
    root.classList.add('is-enhanced', 'is-discovering');

    const dismissDiscovery = () => {
      if (discoveryTimer) {
        window.clearTimeout(discoveryTimer);
        discoveryTimer = 0;
      }
      root.classList.remove('is-discovering');
    };

    discoveryTimer = window.setTimeout(dismissDiscovery, 12000);

    const initialButton = buttons.find((button) => button.dataset.signalState === 'straight'
      || button.dataset.ilbSignal === 'bundled-straight') || buttons[0];
    const initialPanel = panels.find((panel) => panel.dataset.ilbSignalPanel === initialButton.dataset.ilbSignal);
    if (initialPanel) {
      buttons.forEach((button) => {
        const selected = button === initialButton;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      panels.forEach((panel) => panel.classList.toggle('is-active', panel === initialPanel));
    }

    const clearAutoplay = () => {
      if (!autoplayTimer) return;
      window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
    };

    const isScrollModeActive = () => Boolean(scrollTrack
      && autoplayEligible
      && mobileMedia.matches
      && !reduceMotion.matches);

    const isAutoplayContextActive = () => autoplayEligible
      && !reduceMotion.matches
      && !isScrollModeActive()
      && !manualStopped
      && isNearViewport
      && document.visibilityState === 'visible'
      && !isHovered
      && !hasFocusWithin;

    const canAutoplay = () => isAutoplayContextActive()
      && root.getAttribute('aria-busy') !== 'true';

    const getManualStopMessage = () => reduceMotion.matches
      ? 'Automatic rotation remains off while your reduced-motion preference is enabled.'
      : isScrollModeActive()
        ? 'Keep scrolling to continue through the examples.'
      : 'Automatic examples stopped.';

    const getNextButton = () => {
      const currentIndex = buttons.findIndex((button) => button.classList.contains('is-active'));
      for (let offset = 1; offset <= buttons.length; offset += 1) {
        const candidate = buttons[(Math.max(currentIndex, 0) + offset) % buttons.length];
        if (!candidate.disabled) return candidate;
      }
      return null;
    };

    let runAutoplay = () => {};
    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!canAutoplay()) return;
      autoplayTimer = window.setTimeout(() => runAutoplay(), autoplayDelay);
    };

    const selectButton = async (button, { manual = false, scrollDrivenSelection = false } = {}) => {
      if (manual) {
        dismissDiscovery();
        manualStopped = true;
        clearAutoplay();
        requestToken += 1;
        root.removeAttribute('aria-busy');
      }

      const label = button.textContent.trim();
      const key = button.dataset.ilbSignal;
      const panel = panels.find((item) => item.dataset.ilbSignalPanel === key);
      const image = panel && panel.querySelector('img');
      if (!panel || !image) return;

      if (button.classList.contains('is-active')) {
        if (manual) status.textContent = `${label} example selected. ${getManualStopMessage()}`;
        return;
      }

      const token = ++requestToken;
      root.setAttribute('aria-busy', 'true');
      if (manual) status.textContent = `Loading ${label} example…`;

      try {
        const preloader = new Image();
        preloader.src = image.currentSrc || image.src;
        if (typeof preloader.decode === 'function') await preloader.decode();
        else await new Promise((resolve, reject) => {
          preloader.onload = resolve;
          preloader.onerror = reject;
        });
        if (token !== requestToken) return;
        if (!manual && !scrollDrivenSelection && !isAutoplayContextActive()) return;

        buttons.forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        panels.forEach((item) => item.classList.toggle('is-active', item === panel));
        if (manual) {
          status.textContent = manualStopped
            ? `${label} example selected. ${getManualStopMessage()}`
            : `${label} example selected.`;
        }
        else status.textContent = '';
      } catch (error) {
        if (token !== requestToken) return;
        button.disabled = true;
        status.textContent = `${label} example is unavailable. The current example remains visible.`;
        if (manual) button.focus();
      } finally {
        if (token === requestToken) {
          root.removeAttribute('aria-busy');
          if (!scrollDrivenSelection) scheduleAutoplay();
        }
      }
    };

    runAutoplay = () => {
      autoplayTimer = 0;
      if (!canAutoplay()) return;
      const nextButton = getNextButton();
      if (nextButton) void selectButton(nextButton);
    };

    const updateScrollSelection = () => {
      scrollFrameRequest = 0;
      if (!scrollDriven || !scrollTrack) return;

      const stickyTop = Number.parseFloat(window.getComputedStyle(root).top) || 0;
      const trackRect = scrollTrack.getBoundingClientRect();
      const travel = Math.max(1, scrollTrack.offsetHeight - root.offsetHeight);
      const progress = Math.min(1, Math.max(0, (stickyTop - trackRect.top) / travel));
      const nextIndex = Math.min(buttons.length - 1, Math.floor(progress * buttons.length));
      root.style.setProperty('--ilb-reader-scroll-progress', progress.toFixed(4));
      if (nextIndex === scrollDrivenIndex) return;

      scrollDrivenIndex = nextIndex;
      const nextButton = buttons[nextIndex];
      if (!nextButton || nextButton.disabled) return;
      const centeredLeft = nextButton.offsetLeft - ((controls.clientWidth - nextButton.offsetWidth) / 2);
      controls.scrollTo({ left: Math.max(0, centeredLeft), behavior: 'smooth' });
      void selectButton(nextButton, { scrollDrivenSelection: true });
    };

    const scheduleScrollSelection = () => {
      if (!scrollFrameRequest) scrollFrameRequest = window.requestAnimationFrame(updateScrollSelection);
    };

    const setScrollMode = () => {
      scrollDriven = isScrollModeActive();
      root.classList.toggle('is-ilb-reader-scroll-enabled', scrollDriven);
      scrollTrack?.classList.toggle('is-ilb-reader-scroll-enabled', scrollDriven);

      if (scrollDriven) {
        clearAutoplay();
        scheduleScrollSelection();
      } else {
        if (scrollFrameRequest) {
          window.cancelAnimationFrame(scrollFrameRequest);
          scrollFrameRequest = 0;
        }
        scrollDrivenIndex = -1;
        root.style.removeProperty('--ilb-reader-scroll-progress');
        scheduleAutoplay();
      }
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        void selectButton(button, { manual: true });
      }, { signal });
    });

    if (autoplayEligible) {
      const interactionTargets = [root];
      interactionTargets.forEach((target) => {
        target.addEventListener('mouseenter', () => {
          isHovered = true;
          clearAutoplay();
        }, { signal });
        target.addEventListener('mouseleave', () => {
          isHovered = false;
          scheduleAutoplay();
        }, { signal });
        target.addEventListener('focusin', () => {
          hasFocusWithin = true;
          clearAutoplay();
        }, { signal });
        target.addEventListener('focusout', (event) => {
          const nextTarget = event.relatedTarget;
          if (nextTarget && interactionTargets.some((item) => item.contains(nextTarget))) return;
          hasFocusWithin = false;
          scheduleAutoplay();
        }, { signal });
      });
      document.addEventListener('visibilitychange', scheduleAutoplay, { signal });
      const handleReduceMotionChange = () => {
        clearAutoplay();
        setScrollMode();
      };
      if (typeof reduceMotion.addEventListener === 'function') {
        reduceMotion.addEventListener('change', handleReduceMotionChange, { signal });
      } else if (typeof reduceMotion.addListener === 'function') {
        reduceMotion.addListener(handleReduceMotionChange);
        removeReduceMotionListener = () => reduceMotion.removeListener(handleReduceMotionChange);
      }

      mobileMedia.addEventListener('change', setScrollMode, { signal });
      window.addEventListener('scroll', scheduleScrollSelection, { passive: true, signal });
      window.addEventListener('resize', scheduleScrollSelection, { passive: true, signal });

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          const entry = entries.find((item) => item.target === root);
          if (!entry) return;
          isNearViewport = entry.isIntersecting;
          scheduleAutoplay();
        }, { rootMargin: '25% 0px', threshold: 0 });
        observer.observe(root);
      } else {
        const updateViewportState = () => {
          const rect = root.getBoundingClientRect();
          const margin = window.innerHeight * .25;
          isNearViewport = rect.bottom >= -margin && rect.top <= window.innerHeight + margin;
          scheduleAutoplay();
        };
        window.addEventListener('scroll', updateViewportState, { passive: true, signal });
        window.addEventListener('resize', updateViewportState, { passive: true, signal });
        updateViewportState();
      }
      setScrollMode();
    }

    readerRecords.set(root, {
      destroy() {
        controller.abort();
        clearAutoplay();
        dismissDiscovery();
        if (scrollFrameRequest) window.cancelAnimationFrame(scrollFrameRequest);
        if (observer) observer.disconnect();
        removeReduceMotionListener();
        requestToken += 1;
        root.removeAttribute('aria-busy');
        controls.hidden = true;
        root.classList.remove('is-enhanced', 'is-ilb-reader-scroll-enabled');
        scrollTrack?.classList.remove('is-ilb-reader-scroll-enabled');
        root.style.removeProperty('--ilb-reader-scroll-progress');
        readerRecords.delete(root);
      }
    });
  };

  const initYouTube = (root) => {
    if (youtubeRecords.has(root)) return;
    const facade = root.querySelector('[data-ilb-youtube-id]');
    const section = root.closest('[data-ilb-training]');
    const status = section && section.querySelector('[data-ilb-youtube-status]');
    if (!facade || !status) return;

    const controller = new AbortController();
    const { signal } = controller;
    let timeout = 0;
    let iframe = null;

    facade.addEventListener('click', () => {
      if (root.dataset.loading === 'true') return;
      root.dataset.loading = 'true';
      facade.disabled = true;
      status.textContent = 'Loading the on-court video…';

      iframe = document.createElement('iframe');
      iframe.title = 'Official ILoveBasketballTV on-court video';
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(facade.dataset.ilbYoutubeId)}?autoplay=1&playsinline=1&rel=0`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.setAttribute('allowfullscreen', '');

      timeout = window.setTimeout(() => {
        if (iframe) iframe.remove();
        iframe = null;
        facade.disabled = false;
        root.dataset.loading = 'false';
        status.textContent = 'Video unavailable. Try again.';
      }, 12000);

      iframe.addEventListener('load', () => {
        window.clearTimeout(timeout);
        facade.replaceWith(iframe);
        root.dataset.loading = 'false';
        status.textContent = '';
      }, { once: true, signal });
      root.appendChild(iframe);
    }, { once: true, signal });

    youtubeRecords.set(root, {
      destroy() {
        controller.abort();
        window.clearTimeout(timeout);
        if (iframe) iframe.remove();
        youtubeRecords.delete(root);
      }
    });
  };

  const resolveNativeTarget = () => {
    const main = document.querySelector('[data-ilb-main-product]');
    if (!main) return null;
    const info = main.querySelector('[id^="ProductInfo-"]');
    if (!info) return null;
    const fallback = info.querySelector('.product__title h1') || info;
    const submit = info.querySelector('.product-form__submit:not([disabled])');
    if (!submit) return fallback;
    const selector = [
      '.product-form__input select:not([disabled])',
      '.product-form__input input[type="radio"]:not([disabled])',
      '.quantity__input:not([disabled])'
    ].join(',');
    return info.querySelector(selector) || submit;
  };

  const initAction = (link) => {
    if (actionRecords.has(link)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const main = document.querySelector('[data-ilb-main-product]');

    const sync = () => { link.hidden = !resolveNativeTarget(); };
    sync();

    link.addEventListener('click', (event) => {
      const target = resolveNativeTarget();
      if (!target) {
        link.hidden = true;
        return;
      }
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        const naturallyFocusable = target.matches('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]');
        const hadTabindex = target.hasAttribute('tabindex');
        if (!naturallyFocusable) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        if (!naturallyFocusable && !hadTabindex) {
          target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
        }
      }, 450);
    }, { signal });

    const observer = main ? new MutationObserver(sync) : null;
    if (observer) observer.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });

    actionRecords.set(link, {
      destroy() {
        controller.abort();
        if (observer) observer.disconnect();
        actionRecords.delete(link);
      }
    });
  };

  const initWithin = (root = document) => {
    findAll(root, '[data-ilb-scroll-spin]').forEach(initScrollSpin);
    findAll(root, '[data-ilb-hero-scrub]').forEach(initHeroScrub);
    findAll(root, '[data-ilb-anatomy-scrub]').forEach(initAnatomyScrub);
    findAll(root, '[data-ilb-signal-reader]').forEach(initReader);
    findAll(root, '[data-ilb-youtube-frame]').forEach(initYouTube);
    findAll(root, '[data-ilb-product-scroll]').forEach(initAction);
  };

  const destroyWithin = (root) => {
    findAll(root, '[data-ilb-scroll-spin]').forEach((item) => scrollRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-hero-scrub]').forEach((item) => heroScrubRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-anatomy-scrub]').forEach((item) => anatomyScrubRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-signal-reader]').forEach((item) => readerRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-youtube-frame]').forEach((item) => youtubeRecords.get(item)?.destroy());
    findAll(root, '[data-ilb-product-scroll]').forEach((item) => actionRecords.get(item)?.destroy());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initWithin(), { once: true });
  else initWithin();

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
